import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { formatNumber } from "../../utils/helpers";

// Animations
const pulse = keyframes`
  0% { background-color: #1a1a1a; }
  50% { background-color: #252525; }
  100% { background-color: #1a1a1a; }
`;

// Styled Components
const Entry = styled(motion.div)<{
  $isCurrentUser?: boolean;
  $isTop3?: boolean;
}>`
  background: ${(props) =>
    props.$isCurrentUser
      ? "rgba(0, 224, 255, 0.1)"
      : props.$isTop3
      ? "rgba(245, 166, 35, 0.08)"
      : "#1a1a1a"};
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.75rem;
  border: 1px solid
    ${(props) =>
      props.$isCurrentUser
        ? "rgba(0, 224, 255, 0.3)"
        : props.$isTop3
        ? "rgba(245, 166, 35, 0.2)"
        : "rgba(255, 255, 255, 0.08)"};
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  font-size: 0.875rem;

  &:hover {
    background: ${(props) =>
      props.$isCurrentUser
        ? "rgba(0, 224, 255, 0.15)"
        : props.$isTop3
        ? "rgba(245, 166, 35, 0.12)"
        : "#252525"};
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
`;

const RankBadge = styled.div<{ $isTop3: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
  background: ${(props) =>
    props.$isTop3 ? "rgba(245, 166, 35, 0.2)" : "rgba(255, 255, 255, 0.05)"};
  color: ${(props) => (props.$isTop3 ? "#f5a623" : "rgba(255, 255, 255, 0.7)")};
  border: 1px solid
    ${(props) =>
      props.$isTop3 ? "rgba(245, 166, 35, 0.3)" : "rgba(255, 255, 255, 0.1)"};
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 700;
  font-size: 0.875rem;
  background: linear-gradient(135deg, #3a1b5d, #250c3c);
  color: white;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const Username = styled.div<{ $isCurrentUser?: boolean; $isTop3?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) =>
    props.$isCurrentUser ? "#00e0ff" : props.$isTop3 ? "#f5a623" : "white"};
  margin-bottom: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Stat = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  font-size: 0.75rem;
`;

const StatLabel = styled.span`
  color: rgba(255, 255, 255, 0.6);
`;

const StatValue = styled.span<{ $highlight?: boolean }>`
  color: ${(props) => (props.$highlight ? "#ccff00" : "white")};
  font-weight: 600;
`;

const LoadingEntry = styled.div`
  background: #1a1a1a;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  width: 100%;
  height: 64px;
  animation: ${pulse} 1.5s infinite;
  margin-bottom: 0.5rem;
`;

const LeaderboardEntryComponent = ({ user, index, isCurrentUser = false }) => {
  if (!user) {
    return <LoadingEntry />;
  }

  const isTop3 = index < 3;
  const rankEmoji = isTop3 ? ["🥇", "🥈", "🥉"][index] : null;

  return (
    <Entry
      $isCurrentUser={isCurrentUser}
      $isTop3={isTop3}
      whileTap={{ scale: 0.98 }}
    >
      <RankBadge $isTop3={isTop3}>{rankEmoji || `#${index + 1}`}</RankBadge>

      <Avatar>
        {user?.profilePicture ? (
          <AvatarImage src={user.profilePicture} alt="User Avatar" />
        ) : (
          user.username.slice(0, 2).toUpperCase()
        )}
      </Avatar>

      <UserInfo>
        <Username $isCurrentUser={isCurrentUser} $isTop3={isTop3}>
          {user.username}
        </Username>

        <StatsContainer>
          <Stat>
            <StatValue>{formatNumber(user.points || 0)} pts</StatValue>
          </Stat>
        </StatsContainer>
      </UserInfo>
    </Entry>
  );
};

export default LeaderboardEntryComponent;
